import unitsData from './units.json';

// ── State ─────────────────────────────────────────────────────────────────────

let allUnits      = [];
let selectedUnit  = null;
let activeRace    = 'All';
let searchQuery   = '';
let tableVisible  = false;
let sortCol       = null;
let sortDir       = 1; // 1 = asc, -1 = desc

// ── Boot ──────────────────────────────────────────────────────────────────────

async function init() {
    allUnits = unitsData.units;

    buildIconGrid();
    buildTable();
    setupFilters();
    setupTableToggle();
    setupTableSort();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name) {
    const words = name.split(' ');
    return words.length === 1
        ? name.slice(0, 2).toUpperCase()
        : words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function typePill(type) {
    return `<span class="type-pill type-${type}">${type}</span>`;
}

function attackSummary(unit) {
    if (!unit.attacks || unit.attacks.length === 0)
        return `<span class="atk-none">—</span>`;

    const seen = new Set();
    return unit.attacks
        .filter(atk => {
            const key = `${atk.damage}-${atk.bonusVs || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .map(atk => {
            let txt = `<span class="atk-cell">${atk.damage}`;
            if (atk.bonusDamage > 0)
                txt += `<span class="atk-bonus">(+${atk.bonusDamage} vs ${atk.bonusVs})</span>`;
            return txt + '</span>';
        })
        .join('<br>');
}

// ── Matchup computation ───────────────────────────────────────────────────────

/**
 * Can this attack physically reach the target unit?
 * Uses targetableBy: "air" | "ground" | "both"
 * "both" covers the Colossus special case (ground unit targetable by anti-air).
 */
function canTarget(attack, targetUnit) {
    const tgt = targetUnit.targetableBy ?? (targetUnit.isAir ? 'air' : 'ground');
    if (tgt === 'both') return true;
    return attack.targets.includes(tgt);
}

function getMatchups(unit) {
    const goodAgainst = [];
    const weakAgainst = [];

    for (const other of allUnits) {
        if (other.id === unit.id) continue;

        // Bonus damage this unit deals vs other — only if the weapon can reach
        const attackBonuses = unit.attacks
            .filter(a => a.bonusVs && other.types.includes(a.bonusVs) && canTarget(a, other))
            .map(a => `+${a.bonusDamage} vs ${a.bonusVs}`);
        if (attackBonuses.length)
            goodAgainst.push({ unit: other, reason: attackBonuses.join(', ') });

        // Bonus damage other unit deals vs this — only if the weapon can reach
        const defendBonuses = other.attacks
            .filter(a => a.bonusVs && unit.types.includes(a.bonusVs) && canTarget(a, unit))
            .map(a => `+${a.bonusDamage} vs ${a.bonusVs}`);
        if (defendBonuses.length)
            weakAgainst.push({ unit: other, reason: defendBonuses.join(', ') });
    }

    return { goodAgainst, weakAgainst };
}

// ── Icon grid ─────────────────────────────────────────────────────────────────

function buildIconGrid() {
    for (const race of ['Terran', 'Zerg', 'Protoss']) {
        const row = document.getElementById(`icons-${race}`);
        row.innerHTML = '';

        const units = allUnits.filter(u => {
            const raceOk   = (activeRace === 'All' || u.race === activeRace) && u.race === race;
            const searchOk = !searchQuery || u.name.toLowerCase().includes(searchQuery);
            return raceOk && searchOk;
        });

        for (const unit of units) {
            const el = document.createElement('div');
            el.className = 'unit-icon';
            el.dataset.id = unit.id;
            const moveBadge = unit.isAir
                ? `<span class="move-badge air">AIR</span>`
                : `<span class="move-badge ground">GND</span>`;
            el.innerHTML = `
                <img  class="icon-img race-${unit.race}" src="./icons/${unit.id}.svg"
                      alt="${unit.name}" draggable="false"
                      onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
                <div  class="icon-avatar race-${unit.race}" style="display:none">${initials(unit.name)}</div>
                <span class="icon-name">${unit.name}</span>
                ${moveBadge}
            `;
            el.addEventListener('click', () => onUnitClick(unit));
            row.appendChild(el);
        }

        // Show/hide the whole race group based on filter
        const group = document.getElementById(`group-${race}`);
        const hasUnits = units.length > 0;
        group.classList.toggle('hidden', !hasUnits);
    }

    applyIconHighlights();
}

// ── Icon highlight states ─────────────────────────────────────────────────────

function applyIconHighlights() {
    let goodIds = new Set();
    let weakIds = new Set();

    if (selectedUnit) {
        const { goodAgainst, weakAgainst } = getMatchups(selectedUnit);
        goodIds = new Set(goodAgainst.map(e => e.unit.id));
        weakIds = new Set(weakAgainst.map(e => e.unit.id));
    }

    document.querySelectorAll('.unit-icon').forEach(el => {
        const id = el.dataset.id;
        el.classList.remove(
            'icon-selected', 'icon-countered', 'icon-counter', 'icon-both', 'icon-dimmed'
        );

        if (!selectedUnit) return;

        if (id === selectedUnit.id) {
            el.classList.add('icon-selected');
        } else if (goodIds.has(id) && weakIds.has(id)) {
            el.classList.add('icon-both');
        } else if (goodIds.has(id)) {
            el.classList.add('icon-countered');
        } else if (weakIds.has(id)) {
            el.classList.add('icon-counter');
        } else {
            el.classList.add('icon-dimmed');
        }
    });
}

// ── Stats table ───────────────────────────────────────────────────────────────

function sortValue(unit, col) {
    switch (col) {
        case 'name':    return unit.name.toLowerCase();
        case 'race':    return unit.race.toLowerCase();
        case 'hp':      return unit.stats.hp;
        case 'shields': return unit.stats.shields;
        case 'armor':   return unit.stats.armor;
        case 'speed':   return unit.stats.speed;
        default:        return '';
    }
}

function buildTable() {
    const tbody = document.getElementById('units-tbody');
    tbody.innerHTML = '';

    let filtered = allUnits.filter(u => {
        const raceOk   = activeRace === 'All' || u.race === activeRace;
        const searchOk = !searchQuery || u.name.toLowerCase().includes(searchQuery);
        return raceOk && searchOk;
    });

    if (sortCol) {
        filtered = [...filtered].sort((a, b) => {
            const av = sortValue(a, sortCol);
            const bv = sortValue(b, sortCol);
            if (av < bv) return -sortDir;
            if (av > bv) return  sortDir;
            return 0;
        });
    }

    // Update header sort icons
    document.querySelectorAll('#units-table th[data-col]').forEach(th => {
        const icon = th.querySelector('.sort-icon');
        if (!icon) return;
        if (th.dataset.col === sortCol) {
            icon.textContent = sortDir === 1 ? ' ▲' : ' ▼';
            th.classList.add('th-sorted');
        } else {
            icon.textContent = '';
            th.classList.remove('th-sorted');
        }
    });

    for (const unit of filtered) {
        const tr = document.createElement('tr');
        tr.dataset.id = unit.id;

        const shieldsVal = unit.stats.shields > 0
            ? `<span class="stat-shield">${unit.stats.shields}</span>`
            : `<span class="stat-muted">—</span>`;

        tr.innerHTML = `
            <td>
                <div class="unit-name-cell">
                    <div class="unit-avatar race-${unit.race}">${initials(unit.name)}</div>
                    <span class="unit-label">${unit.name}</span>
                </div>
            </td>
            <td><span class="race-badge race-${unit.race}">${unit.race}</span></td>
            <td><span class="stat-hp">${unit.stats.hp}</span></td>
            <td>${shieldsVal}</td>
            <td>${unit.stats.armor}</td>
            <td>${unit.stats.speed}</td>
            <td><div class="types-cell">${unit.types.map(typePill).join('')}</div></td>
            <td>${attackSummary(unit)}</td>
        `;

        tr.addEventListener('click', () => onUnitClick(unit));
        tbody.appendChild(tr);
    }

    applyTableHighlights();
}

function applyTableHighlights() {
    let goodIds = new Set();
    let weakIds = new Set();

    if (selectedUnit) {
        const { goodAgainst, weakAgainst } = getMatchups(selectedUnit);
        goodIds = new Set(goodAgainst.map(e => e.unit.id));
        weakIds = new Set(weakAgainst.map(e => e.unit.id));
    }

    document.querySelectorAll('#units-tbody tr').forEach(row => {
        const id = row.dataset.id;
        row.classList.remove('row-selected', 'row-counter', 'row-countered', 'row-both');
        if (!selectedUnit) return;

        if (id === selectedUnit.id)           row.classList.add('row-selected');
        else if (goodIds.has(id) && weakIds.has(id)) row.classList.add('row-both');
        else if (goodIds.has(id))             row.classList.add('row-countered');
        else if (weakIds.has(id))             row.classList.add('row-counter');
    });
}

// ── Unit click ────────────────────────────────────────────────────────────────

function onUnitClick(unit) {
    if (selectedUnit && selectedUnit.id === unit.id) {
        selectedUnit = null;
        applyIconHighlights();
        applyTableHighlights();
        document.getElementById('unit-card').classList.add('hidden');
        return;
    }
    selectedUnit = unit;
    applyIconHighlights();
    applyTableHighlights();
    renderDetailCard(unit);
}

// ── Detail card ───────────────────────────────────────────────────────────────

function renderDetailCard(unit) {
    const card = document.getElementById('unit-card');
    card.classList.remove('hidden');

    const { goodAgainst, weakAgainst } = getMatchups(unit);
    const { cost, stats } = unit;

    const attackRows = (unit.attacks && unit.attacks.length > 0)
        ? unit.attacks.map(atk => {
            const targets = atk.targets;
            let targetLabel, targetClass;
            if (targets.includes('ground') && targets.includes('air')) {
                targetLabel = 'Ground + Air'; targetClass = 'both';
            } else if (targets.includes('air')) {
                targetLabel = 'Air'; targetClass = 'air';
            } else {
                targetLabel = 'Ground'; targetClass = 'ground';
            }
            const bonusTag = atk.bonusDamage > 0
                ? `<span class="atk-bonus-tag">+${atk.bonusDamage} vs ${atk.bonusVs}</span>` : '';
            const weaponName = atk.name
                ? `<span class="atk-name">${atk.name}</span>` : '';
            return `<div class="attack-row">
                ${weaponName}
                <span class="atk-dmg">${atk.damage} dmg</span>
                ${bonusTag}
                <span class="atk-range">Range ${atk.range}</span>
                <span class="atk-target ${targetClass}">${targetLabel}</span>
            </div>`;
        }).join('')
        : '';

    const attacksSection = attackRows
        ? `<div class="section-title">Weapons</div><div class="attacks-list">${attackRows}</div>`
        : '';

    const abilityRows = (unit.abilities && unit.abilities.length > 0)
        ? unit.abilities.map(a =>
            `<div class="ability-row">
                <span class="ability-name">${a.name}</span>
                ${a.description ? `<span class="ability-desc">${a.description}</span>` : ''}
            </div>`
        ).join('')
        : '';

    const abilitiesSection = abilityRows
        ? `<div class="section-title">Abilities</div><div class="abilities-list">${abilityRows}</div>`
        : '';

    function matchupCard(entry, cls) {
        const u = entry.unit;
        return `<div class="matchup-unit ${cls}" data-id="${u.id}">
            <img  class="mu-icon race-${u.race}" src="./icons/${u.id}.svg"
                  alt="${u.name}" draggable="false"
                  onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
            <div  class="mu-avatar race-${u.race}" style="display:none">${initials(u.name)}</div>
            <div class="mu-info">
                <div class="mu-name">${u.name}</div>
                <div class="mu-reason">${entry.reason}</div>
            </div>
            <span class="race-badge race-${u.race}" style="flex-shrink:0">${u.race[0]}</span>
        </div>`;
    }

    const goodHTML = goodAgainst.length
        ? goodAgainst.map(e => matchupCard(e, 'good-match')).join('')
        : '<div class="no-matchup">No bonus damage vs any unit type</div>';

    const weakHTML = weakAgainst.length
        ? weakAgainst.map(e => matchupCard(e, 'bad-match')).join('')
        : '<div class="no-matchup">No units deal bonus damage vs this unit</div>';

    card.innerHTML = `
        <div class="card-header">
            <img  class="card-icon race-${unit.race}" src="./icons/${unit.id}.svg"
                  alt="${unit.name}" draggable="false"
                  onerror="this.style.display='none';this.nextElementSibling.style.display='flex'">
            <div  class="card-avatar race-${unit.race}" style="display:none">${initials(unit.name)}</div>
            <div class="card-title">
                <h2>${unit.name}</h2>
                <div style="margin-top:4px"><span class="race-badge race-${unit.race}">${unit.race}</span></div>
                <div class="card-types">${unit.types.map(typePill).join('')}</div>
            </div>
        </div>
        <div class="card-body">
            <div class="stats-grid">
                <div class="stat-box defense-box">
                    <div class="stat-label">Defense</div>
                    <div class="defense-lines">
                        <div class="defense-line"><span class="stat-hp">${stats.hp}</span><span class="defense-tag">HP</span></div>
                        ${stats.shields > 0 ? `<div class="defense-line"><span class="stat-shield">${stats.shields}</span><span class="defense-tag">Shields</span></div>` : ''}
                        <div class="defense-line"><span class="defense-armor">${stats.armor}</span><span class="defense-tag">Armor</span></div>
                    </div>
                </div>
                <div class="stat-box move-box">
                    <div class="stat-label">Movement</div>
                    <div class="defense-lines">
                        <div class="defense-line"><span class="${unit.isAir ? 'move-air' : 'move-ground'}">${unit.isAir ? 'Air' : 'Ground'}${unit.targetableBy === 'both' ? ' *' : ''}</span></div>
                        <div class="defense-line"><span class="defense-speed">${stats.speed}</span><span class="defense-tag">Speed</span></div>
                    </div>
                </div>
                <div class="stat-box cost-box">
                    <div class="stat-label">Cost</div>
                    <div class="cost-lines">
                        <div class="cost-line"><span class="cost-val cost-minerals">${cost.minerals}</span><span class="cost-tag">minerals</span></div>
                        <div class="cost-line"><span class="cost-val cost-gas">${cost.gas}</span><span class="cost-tag">gas</span></div>
                        <div class="cost-line"><span class="cost-val cost-supply">${cost.supply}</span><span class="cost-tag">supply</span></div>
                        <div class="cost-line"><span class="cost-val cost-time">${cost.buildTime}s</span><span class="cost-tag">build time</span></div>
                    </div>
                </div>
            </div>
            ${attacksSection}
            ${abilitiesSection}
            <div class="matchup-grid">
                <div>
                    <div class="matchup-title good">Counters <span class="count">(${goodAgainst.length})</span></div>
                    <div class="matchup-cards">${goodHTML}</div>
                </div>
                <div>
                    <div class="matchup-title bad">Countered by <span class="count">(${weakAgainst.length})</span></div>
                    <div class="matchup-cards">${weakHTML}</div>
                </div>
            </div>
        </div>
    `;

    card.querySelectorAll('.matchup-unit').forEach(el => {
        el.addEventListener('click', () => {
            const u = allUnits.find(u => u.id === el.dataset.id);
            if (u) onUnitClick(u);
        });
    });

    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

// ── Filters ───────────────────────────────────────────────────────────────────

function setupFilters() {
    document.querySelectorAll('.filter-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            activeRace = btn.dataset.race;
            buildIconGrid();
            buildTable();
        });
    });

    document.getElementById('search').addEventListener('input', e => {
        searchQuery = e.target.value.trim().toLowerCase();
        buildIconGrid();
        buildTable();
    });
}

// ── Table toggle ──────────────────────────────────────────────────────────────

function setupTableToggle() {
    const btn     = document.getElementById('table-toggle-btn');
    const section = document.getElementById('table-section');
    const label   = document.getElementById('toggle-label');

    btn.addEventListener('click', () => {
        tableVisible = !tableVisible;
        section.classList.toggle('hidden', !tableVisible);
        btn.classList.toggle('open', tableVisible);
        label.textContent = tableVisible ? 'Hide stats table' : 'Show stats table';
    });
}

// ── Table sort ────────────────────────────────────────────────────────────────

function setupTableSort() {
    document.querySelectorAll('#units-table th[data-col]').forEach(th => {
        th.addEventListener('click', () => {
            const col = th.dataset.col;
            if (sortCol === col) {
                sortDir *= -1;
            } else {
                sortCol = col;
                sortDir = 1;
            }
            buildTable();
            applyTableHighlights();
        });
    });
}

// ── Start ─────────────────────────────────────────────────────────────────────

init();
